import { A, useLocation } from "@solidjs/router";
import { Component, For, Show, createSignal } from "solid-js";
import { IdBadge } from "./IdBadge";
import { useBranding } from "./BrandingProvider";
import { useAuth } from "../store/authStore";
import { trace } from "../lib/trace";
import { NavIcon, NavIconName } from "./NavIcon";

export interface SidebarSection {
  title: string;
  items: SidebarItem[];
  adminOnly?: boolean;
}

export interface SidebarItem {
  path: string;
  label: string;
  icon: NavIconName;
  badge?: string;
  adminOnly?: boolean;
}

export interface LeftSidebarProps {
  sections: SidebarSection[];
  accountContext?: {
    id: string;
    name: string;
  } | null;
}

export const LeftSidebar: Component<LeftSidebarProps> = (props) => {
  const location = useLocation();
  const { isAdmin } = useAuth();
  const [isMobileOpen, setIsMobileOpen] = createSignal(false);
  trace.navigation("LeftSidebar rendered", { sectionCount: props.sections.length, hasAccountContext: !!props.accountContext });

  const isActive = (path: string) => {
    // Exact match for dashboard-type paths (avoid highlighting dashboard when on sub-pages)
    if (path === "/dashboard" || path.match(/^\/clients\/[^/]+$/)) {
      return location.pathname === path;
    }
    // Prefix match for other paths
    return location.pathname.startsWith(path);
  };

  const sectionHasActive = (items: SidebarItem[]) => items.some(item => isActive(item.path));

  const shouldShowSection = (section: SidebarSection) => {
    if (section.adminOnly && !isAdmin()) return false;
    return true;
  };

  const shouldShowItem = (item: SidebarItem) => {
    if (item.adminOnly && !isAdmin()) return false;
    return true;
  };

  return (
    <>
      {/* Mobile Hamburger Button */}
      <button
        class='lsb-mobile-toggle'
        onClick={() => setIsMobileOpen(!isMobileOpen())}
        aria-label={isMobileOpen() ? "Close navigation menu" : "Open navigation menu"}
        aria-expanded={isMobileOpen()}
      >
        {isMobileOpen() ? "\u2715" : "\u2630"}
      </button>

      {/* Sidebar */}
      <div class={`lsb ${isMobileOpen() ? "lsb--mobile-open" : ""}`}>
        {/* Logo */}
        <div class="lsb__logo">
          <img
            src={useBranding().branding().logoUrl || '/logo.png'}
            alt={useBranding().branding().brandName || 'Airbrx'}
            class="lsb__logo-img"
          />
        </div>

        {/* Mode indicator */}
        <div class="lsb__mode-label">
          {props.accountContext ? props.accountContext.name : "PLATFORM"}
        </div>

        {/* Account Context Header - shown when viewing a specific account */}
        <Show when={props.accountContext}>
          <div class={`lsb__account lsb__account-header--active`}>
            <div class="lsb__account-label">
              Account
            </div>
            <div class="lsb__account-row">
              <div class="lsb__account-avatar">
                {props.accountContext?.name?.charAt(0).toUpperCase() || "A"}
              </div>
              <div class="lsb__account-info">
                <div class="lsb__account-name">
                  {props.accountContext?.name}
                </div>
                <div class="lsb__account-id">
                  <IdBadge id={props.accountContext!.id} />
                </div>
              </div>
            </div>
          </div>
        </Show>

        {/* Navigation Sections */}
        <nav aria-label="Main navigation" class="lsb__nav">
          <For each={props.sections.filter(shouldShowSection)}>
            {(section, sectionIndex) => {
              // Check if this is the Admin Dashboard section (no title, first section when in account context)
              const isAdminDashboardSection = () => section.title === "" && props.accountContext && sectionIndex() === 0;

              return (
                <div class={`nav-section ${sectionHasActive(section.items) ? "nav-section--active" : ""}`}>
                  <Show when={section.title}>
                    <div class="lsb__section-title">
                      {section.title}
                    </div>
                  </Show>
                  <div class="lsb__section-items">
                    <For each={section.items.filter(shouldShowItem)}>
                      {(item) => {
                        // Special styling for Admin Dashboard link
                        const isAdminDashboard = isAdminDashboardSection() && item.label === "Admin Dashboard";

                        return (
                          <A
                            href={item.path}
                            aria-current={isActive(item.path) ? "page" : undefined}
                            class={`lsb__link ${isActive(item.path) ? "lsb__link--active" : ""} ${isAdminDashboard ? "lsb__link--admin-dash" : ""}`}
                            style={{
                              color: isAdminDashboard
                                ? "var(--text-secondary)"
                                : isActive(item.path)
                                  ? "var(--brand-primary)"
                                  : "var(--text-primary)",
                              background: isAdminDashboard
                                ? "var(--bg-primary)"
                                : isActive(item.path)
                                  ? "color-mix(in srgb, var(--brand-primary) 10%, transparent)"
                                  : "transparent",
                              "font-weight": isActive(item.path) ? "600" : "500",
                              "border-left": isAdminDashboard
                                ? "none"
                                : isActive(item.path)
                                  ? "3px solid var(--brand-primary)"
                                  : "3px solid transparent",
                            }}
                            onMouseEnter={(e) => {
                              if (!isActive(item.path)) {
                                e.currentTarget.style.background = isAdminDashboard
                                  ? "var(--bg-secondary)"
                                  : "var(--bg-primary)";
                                if (isAdminDashboard) {
                                  e.currentTarget.style.borderColor = "var(--border-medium)";
                                }
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isActive(item.path)) {
                                e.currentTarget.style.background = isAdminDashboard
                                  ? "var(--bg-primary)"
                                  : "transparent";
                                if (isAdminDashboard) {
                                  e.currentTarget.style.borderColor = "var(--border-light)";
                                }
                              }
                            }}>
                            <div class="lsb__link-content">
                              <NavIcon name={item.icon} size={18} class={`lsb__link-icon ${isAdminDashboard ? "lsb__link-icon--admin-dash" : ""}`} />
                              <span>{item.label}</span>
                            </div>
                            <Show when={item.badge}>
                              <span class="lsb__badge">
                                {item.badge}
                              </span>
                            </Show>
                            <Show when={isAdminDashboard}>
                              <span class="lsb__back-arrow">&larr;</span>
                            </Show>
                          </A>
                        );
                      }}
                    </For>
                  </div>
                </div>
              );
            }}
          </For>
        </nav>

        {/* Back to Platform link - shown when in account context */}
        <Show when={props.accountContext}>
          <div class="lsb__back-link">
            <A href="/dashboard" class="lsb__link">&larr; Back to Platform</A>
          </div>
        </Show>

        {/* Mobile Overlay */}
        <Show when={isMobileOpen()}>
          <div
            class='lsb__overlay'
            onClick={() => setIsMobileOpen(false)}
          />
        </Show>
      </div>

      {/* Responsive styles now in global.css */}
    </>
  );
};
